import { z } from "zod";
import type { PickscapeProject } from "../types";

const sourceSchema = z.object({
  kind: z.enum(["paste", "txt", "csv", "tsv", "xlsx", "json"]),
  fileName: z.string().optional(),
  sheet: z.string().optional(),
  row: z.number().int().positive(),
});

const entrySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(120),
  category: z.string().max(80).nullable(),
  weight: z.number().int().min(1).max(1_000),
  enabled: z.boolean(),
  source: sourceSchema,
});

const historySchema = z.object({
  id: z.string().min(1),
  drawNumber: z.number().int().positive(),
  timestamp: z.string(),
  winnerId: z.string(),
  winnerName: z.string(),
  winnerCategory: z.string().nullable(),
  winnerWeight: z.number().int().min(1).max(1_000),
  eligibleCount: z.number().int().positive(),
  categoryFilter: z.array(z.string()),
  repeatMode: z.enum(["repeat", "no-repeat"]),
  keptInPool: z.boolean().default(false),
  stopReason: z.enum(["manual", "auto-30s", "auto-duration"]),
  durationMs: z.number().nonnegative(),
});

const projectSchema = z.object({
  schemaVersion: z.literal(1),
  listName: z.string(),
  entries: z.array(entrySchema).max(10_000),
  history: z.array(historySchema),
  settings: z.object({
    theme: z.enum(["star-voyage", "code-rain", "kinetic-cloud", "wheel"]),
    appearance: z.enum(["system", "dark", "light"]),
    durationSeconds: z.number().int().min(5).max(60),
    soundEnabled: z.boolean(),
    soundPack: z.enum(["cinematic", "party", "rock", "arcade"]).default("cinematic"),
    celebrationVoice: z
      .enum(["auto", "female", "male", "crowd", "woohoo", "congratulations", "off"])
      .default("auto"),
    musicGenre: z
      .enum(["follow-pack", "cinematic", "party", "rock", "arcade", "off"])
      .default("follow-pack"),
    musicVolume: z.number().int().min(0).max(100).default(58),
    motionReduced: z.boolean(),
    repeatMode: z.enum(["repeat", "no-repeat"]),
    selectedCategories: z.array(z.string()),
  }),
  updatedAt: z.string(),
});

export function parseProjectBackup(text: string): PickscapeProject {
  const raw = JSON.parse(text) as unknown;
  return projectSchema.parse(raw) as PickscapeProject;
}
